/**
 * Liste de opțiuni definibile din Setări (categoria „Liste & opțiuni").
 *
 * Sursa de adevăr pentru DEFAULT-uri + tipuri + resolveri. Setările propriu-zise
 * (chei `options.*`) trăiesc în `SETTINGS_REGISTRY` ca `type: 'json'` cu forma declarată.
 * Fronturile citesc valorile curente prin `GET /admin/settings` și cad pe aceste
 * default-uri când setarea nu e suprascrisă.
 *
 * DEFAULT-urile sunt SINCRONIZATE cu:
 *  - `ProviderRegistry` (ce provideri rezolvă codul per fel de pas)
 *  - seed-ul de workflow-uri (`workflow.seed-data.ts`) și voci (`voice.seed-data.ts`)
 *  - default-urile din providerii reali (`real/llm.providers.ts`, `real/media.providers.ts`)
 * ca să nu apară valori „necunoscute".
 */
/** Cheile setărilor de tip listă (namespace `options.*`). */
export const OPTION_SETTING_KEYS = {
    providersByStep: 'options.providers_by_step',
    modelsByProvider: 'options.models_by_provider',
    catalogBadges: 'options.catalog_badges',
    templateNiches: 'options.template_niches',
    musicMoods: 'options.music_moods',
    voiceStyles: 'options.voice_styles',
};
/**
 * Providerii disponibili per tip de pas — exact ce rezolvă `ProviderRegistry`:
 *  orchestrator(llm): openai/anthropic/gemini
 *  image_generate/edit: bfl + grok (Imagine) + openai
 *  image_to_video: grok · tts: elevenlabs/openai/gemini · voice_change: elevenlabs
 */
export const DEFAULT_PROVIDERS_BY_STEP = {
    orchestrator: ['openai', 'anthropic', 'gemini'],
    image_generate: ['bfl', 'grok', 'openai'],
    image_edit: ['bfl', 'grok', 'openai'],
    image_to_video: ['grok'],
    tts: ['elevenlabs', 'openai', 'gemini'],
    voice_change: ['elevenlabs'],
};
/**
 * Modele per provider (actualizat iul. 2026) — mix calitate/preț pentru pipeline.
 *  - OpenAI GPT-5.6: Luna (volum), Terra (echilibru), Sol/gpt-5.6 (flagship).
 *    Familia GPT-5.x nu folosește `temperature` ca GPT-4o.
 *  - Anthropic: Sonnet 5 (workhorse), Haiku 4.5 (ieftin), Opus 4.8 (scump).
 *  - Gemini: 3.5 Flash (agentic/cost), 3.1 Pro (reasoning), TTS pe 2.5 Flash.
 *  - Grok video: Imagine Video (+ 1.5 mai scump).
 *  - BFL: FLUX.2 Pro (producție), Klein 9B (ieftin/rapid).
 *  - ElevenLabs: v3 (flagship) + Flash (latency/cost).
 */
export const DEFAULT_MODELS_BY_PROVIDER = {
    openai: [
        { value: 'gpt-5.6-luna', label: 'GPT-5.6 Luna (rentabil)' },
        { value: 'gpt-5.6-terra', label: 'GPT-5.6 Terra (echilibru)' },
        { value: 'gpt-5.6', label: 'GPT-5.6 Sol (flagship)' },
        { value: 'gpt-image-1', label: 'GPT Image 1' },
        { value: 'dall-e-3', label: 'DALL·E 3' },
        { value: 'gpt-4o-mini-tts', label: 'GPT-4o mini TTS (legacy speech)' },
    ],
    anthropic: [
        { value: 'claude-sonnet-5', label: 'Claude Sonnet 5' },
        { value: 'claude-haiku-4-5', label: 'Claude Haiku 4.5' },
        { value: 'claude-opus-4-8', label: 'Claude Opus 4.8' },
    ],
    gemini: [
        { value: 'gemini-3.5-flash', label: 'Gemini 3.5 Flash' },
        { value: 'gemini-3.1-pro-preview', label: 'Gemini 3.1 Pro' },
        { value: 'gemini-2.5-flash-preview-tts', label: 'Gemini 2.5 Flash TTS' },
        { value: 'gemini-2.5-pro-preview-tts', label: 'Gemini 2.5 Pro TTS' },
    ],
    grok: [
        { value: 'grok-imagine-image-quality', label: 'Grok Imagine Image Quality' },
        { value: 'grok-imagine-image', label: 'Grok Imagine Image (rapid)' },
        { value: 'grok-imagine-image-pro', label: 'Grok Imagine Image Pro' },
        { value: 'grok-imagine-video', label: 'Grok Imagine Video' },
        { value: 'grok-imagine-video-1.5', label: 'Grok Imagine Video 1.5' },
        { value: 'avatar-v2', label: 'Avatar v2 (prezentator)' },
    ],
    bfl: [
        { value: 'flux-2-pro', label: 'FLUX.2 Pro' },
        { value: 'flux-2-pro-preview', label: 'FLUX.2 Pro (preview)' },
        { value: 'flux-2-klein-9b', label: 'FLUX.2 Klein 9B (rapid)' },
        { value: 'flux-1.1-pro', label: 'FLUX 1.1 Pro (legacy)' },
    ],
    elevenlabs: [
        { value: 'eleven_v3', label: 'Eleven v3' },
        { value: 'eleven_flash_v2_5', label: 'Eleven Flash v2.5 (rapid)' },
        { value: 'eleven_multilingual_v2', label: 'Eleven Multilingual v2' },
        { value: 'voice-changer-v2', label: 'Voice Changer v2' },
    ],
};
/** Badge-uri pentru SKU-uri (design/seed: „Recomandat"; restul — sugestii). */
export const DEFAULT_CATALOG_BADGES = [
    { value: 'Recomandat', label: 'Recomandat' },
    { value: 'Cel mai vândut', label: 'Cel mai vândut' },
    { value: 'Popular', label: 'Popular' },
    { value: 'Nou', label: 'Nou' },
];
/** Nișe de șabloane (câmp text liber azi; sugestii pentru combobox creatable). */
export const DEFAULT_TEMPLATE_NICHES = [
    'HoReCa',
    'eCommerce',
    'Beauty & Cosmetice',
    'Fitness & Wellness',
    'Imobiliare',
    'Auto',
    'Fashion',
    'Servicii',
    'Educație',
    'Tech & Gadgeturi',
];
/** Mood-uri muzică (câmp text liber azi). */
export const DEFAULT_MUSIC_MOODS = [
    'energic',
    'calm',
    'corporate',
    'epic',
    'vesel',
    'dramatic',
    'inspirațional',
    'lofi',
];
/** Stiluri de voce (câmp text liber azi). */
export const DEFAULT_VOICE_STYLES = [
    'energic',
    'calm',
    'prietenos',
    'profesional',
    'vânzare',
    'narativ',
    'entuziast',
];
/** Providerii de voce disponibili (aliniat cu `VOICE_PROVIDERS`). */
export const DEFAULT_VOICE_PROVIDERS = ['elevenlabs', 'openai', 'gemini'];
/** Providerii cunoscuți de sistem (pentru câmpul „Provider" din chei) + smtp. */
export const KNOWN_SECRET_PROVIDERS = [
    'openai',
    'anthropic',
    'gemini',
    'grok',
    'bfl',
    'elevenlabs',
    'stripe',
    'r2',
    'smtp',
];
export const IMAGE_STUDIO_PROVIDERS = [
    {
        id: 'grok',
        label: 'Grok Imagine (xAI)',
        description: 'Imagine Image Quality / Pro — text→image, edit pe 1–3 referințe (păstrează identitatea).',
        secretProvider: 'grok',
        defaultModel: 'grok-imagine-image-quality',
        supportsGenerate: true,
        supportsEdit: true,
        maxEditRefs: 3,
        costHint: '~$0.01–0.07 / img',
        models: [
            { value: 'grok-imagine-image-quality', label: 'Imagine Quality', description: 'Calitate maximă, multi-ref edit', supportsEdit: true },
            { value: 'grok-imagine-image', label: 'Imagine (rapid)', description: 'Mai ieftin / rapid', supportsEdit: true },
            { value: 'grok-imagine-image-pro', label: 'Imagine Pro', description: 'Pro tier', supportsEdit: true },
        ],
    },
    {
        id: 'bfl',
        label: 'Black Forest Labs (Flux)',
        description: 'FLUX.2 Pro / Klein — generate + edit pe o referință. Standardul pipeline-ului de clipuri.',
        secretProvider: 'bfl',
        defaultModel: 'flux-2-pro',
        supportsGenerate: true,
        supportsEdit: true,
        maxEditRefs: 1,
        costHint: 'per request BFL',
        models: [
            { value: 'flux-2-pro', label: 'FLUX.2 Pro', supportsEdit: true },
            { value: 'flux-2-pro-preview', label: 'FLUX.2 Pro preview', supportsEdit: true },
            { value: 'flux-2-klein-9b', label: 'FLUX.2 Klein 9B (rapid)', supportsEdit: true },
            { value: 'flux-1.1-pro', label: 'FLUX 1.1 Pro (legacy)', supportsEdit: true },
        ],
    },
    {
        id: 'openai',
        label: 'OpenAI Images',
        description: 'GPT Image 1 / DALL·E 3. Edit pe o referință (GPT Image 1).',
        secretProvider: 'openai',
        defaultModel: 'gpt-image-1',
        supportsGenerate: true,
        supportsEdit: true,
        maxEditRefs: 1,
        costHint: 'per OpenAI pricing',
        models: [
            { value: 'gpt-image-1', label: 'GPT Image 1', supportsEdit: true },
            { value: 'dall-e-3', label: 'DALL·E 3', supportsEdit: false },
        ],
    },
    {
        id: 'mock',
        label: 'Mock (dev)',
        description: 'Imagini placeholder — fără cheie, instant. Multi-ref e simulat (folosește prima).',
        secretProvider: null,
        defaultModel: 'mock',
        supportsGenerate: true,
        supportsEdit: true,
        maxEditRefs: 3,
        costHint: 'gratuit',
        models: [{ value: 'mock', label: 'Mock PNG', supportsEdit: true }],
    },
];
// --- Resolveri (frontend): valoare curentă din settings-map cu fallback pe default ---
export function resolveProvidersForStep(map, step) {
    const fromMap = map?.[step];
    if (Array.isArray(fromMap) && fromMap.length > 0)
        return fromMap;
    return DEFAULT_PROVIDERS_BY_STEP[step] ?? [];
}
export function resolveModelsForProvider(map, provider) {
    const key = provider.trim().toLowerCase();
    const fromMap = map?.[key];
    if (Array.isArray(fromMap) && fromMap.length > 0)
        return fromMap;
    return DEFAULT_MODELS_BY_PROVIDER[key] ?? [];
}
//# sourceMappingURL=options.js.map